import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const PRIMARY = '#017e6e';

const TopProgressBar: React.FC = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const prevPath = useRef(location.pathname + location.search);

  const clear = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const start = () => {
    clear();
    setProgress(0);
    setVisible(true);

    // Animate quickly to 80%, then slow down
    let p = 0;
    const tick = () => {
      p = p < 70 ? p + 3 : p < 85 ? p + 0.5 : p + 0.1;
      if (p < 90) {
        setProgress(p);
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    // Complete after 400ms
    timerRef.current = setTimeout(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setProgress(100);
      timerRef.current = setTimeout(() => setVisible(false), 300);
    }, 400);
  };

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    if (currentPath !== prevPath.current) {
      prevPath.current = currentPath;
      start();
    }
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="progress-bar"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            background: 'transparent',
            pointerEvents: 'none',
          }}
        >
          <motion.div
            style={{
              height: '100%',
              background: PRIMARY,
              boxShadow: `0 0 8px ${PRIMARY}`,
              width: `${progress}%`,
              transition: 'width 0.1s linear',
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TopProgressBar;
